import { ethers, utils } from "ethers";
import vaultAbi from "../../abi/balanceVault.json";
import erc20Abi from "../../abi/erc20.json";
import {
  showErrorAlert as showAlert,
  showErrorAlert,
  showPendingAlert,
} from "../showAlert";
import { ALL_VAULTS } from "../constant/vaults";
import * as Sentry from "@sentry/react";

export const depositTokenIntoBalanceVault = async (
  userAddress: string,
  tokenAddress: string,
  tokenDecimal: number,
  amount: number,
  chainId: number,
  provider: any
) => {
  let res;

  const signer = provider.getSigner(userAddress);
  const vault = new ethers.Contract(ALL_VAULTS[chainId], vaultAbi, signer);

  const depositAmount = utils.parseUnits(amount.toString(), tokenDecimal);

  const token = new ethers.Contract(tokenAddress, erc20Abi, signer);
  const tokenBalance = await token.balanceOf(userAddress);
  const allowance = await token.allowance(userAddress, ALL_VAULTS[chainId]);

  if (tokenBalance.lt(depositAmount)) {
    showAlert("Your balance is not enough.");
  } else {
    if (allowance.lt(depositAmount)) {
      try {
        const approveRes = await token.approve(
          ALL_VAULTS[chainId],
          depositAmount
        );
        await showPendingAlert(approveRes.hash, `Permission Granted`, provider);
      } catch (error: any) {
        if (error.message.includes("User denied transaction signature")) {
          showErrorAlert("You denied the transaction.");
        } else {
          showErrorAlert("Grant Permission Failed");
        }
        return;
      }
    }
    try {
      res = await vault.deposit(tokenAddress, depositAmount);
      await showPendingAlert(res.hash, "Deposit Succeed", provider);
    } catch (error: any) {
      if (error.message.includes("User denied transaction signature")) {
        showErrorAlert("You denied the transaction.");
      } else {
        showErrorAlert("Deposit Failed");
      }
    }
  }

  return res;
};

export const withdrawTokenFromBalanceVault = async (
  userAddress: string,
  tokenAddress: string,
  tokenDecimal: number,
  amount: number,
  chainId: number,
  provider: any
) => {
  let res;

  const withdrawAmount = utils.parseUnits(amount.toString(), tokenDecimal);

  const signer = provider.getSigner(userAddress);
  const vault = new ethers.Contract(ALL_VAULTS[chainId], vaultAbi, signer);

  const accountBalance = await vault.getAccountBalance(
    userAddress,
    tokenAddress
  );

  if (accountBalance.lt(withdrawAmount)) {
    showAlert("Your account balance is not enough.");
  } else {
    try {
      res = await vault.withdraw(tokenAddress, withdrawAmount);
      await showPendingAlert(res.hash, "Withdraw Succeed", provider);
    } catch (error: any) {
      if (error.message.includes("User denied transaction signature")) {
        showErrorAlert("You denied the transaction.");
      } else {
        showErrorAlert("Withdraw Failed");
      }
    }
  }

  return res;
};

export const getAccountBalanceFromBalanceVault = async (
  userAddress: string,
  tokenAddress: string,
  chainId: number,
  provider: any
) => {
  let amount;

  const signer = provider.getSigner(userAddress);
  const vault = new ethers.Contract(ALL_VAULTS[chainId], vaultAbi, signer);

  try {
    amount = await vault.getAccountBalance(userAddress, tokenAddress);
  } catch (error) {
    console.log(error);
  }

  return amount;
};

export const approveFactoryToUseBalanceVault = async (
  userAddress: string,
  factoryAddress: string,
  tokenAddress: string,
  chainId: number,
  provider: any
) => {
  const signer = provider.getSigner(userAddress);
  const balanceVault = new ethers.Contract(
    ALL_VAULTS[chainId],
    vaultAbi,
    signer
  );

  try {
    const approveRes = await balanceVault.approve(factoryAddress, tokenAddress);
    await showPendingAlert(
      approveRes.hash,
      `Approved Factory Succeed. You can now open the position!`,
      provider
    );
  } catch (e: any) {
    if (e.code === "ACTION_REJECTED") {
      showErrorAlert("You denied the transaction.");
    } else {
      showErrorAlert("Approve Factory Failed.");
      Sentry.captureException(e);
    }
  }
};

export const getIsUserApprovedFactory = async (
  userAddress: string,
  factoryAddress: string,
  chainId: number,
  provider: any
) => {
  let isApproved: boolean;

  const signer = provider.getSigner(userAddress);
  const balanceVault = new ethers.Contract(
    ALL_VAULTS[chainId],
    vaultAbi,
    signer
  );

  isApproved = await balanceVault.userApproveFactories(
    userAddress,
    factoryAddress
  );

  return isApproved;
};
